import React, { FC } from 'react';
import Modal from '@uikit/Modal/Modal';
import { useCreateUpdateTransactionsHistoryMutation } from '@store/rtk/members';
import { useLazyGetBusinessTypesQuery } from '@store/rtk/selects';
import { useAppSelector } from '@store/hooks';
import { selectSelects } from '@store/slices/selects/selects';
import { useForm } from '@hooks/useDirtyForm';
import {
  ControlledDateSelect,
  ControlledInput,
  ControlledSelect,
} from '@uikit/FormItems/ControlledItems';
import { validations } from '@utils/validations';
import {
  maskedInputs,
  selectFields,
  TransactionModalData,
  TransactionModalDefaultValues,
} from './constants';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import LimitationsSkeleton from '@uikit/Loader/LimitationsSkeleton/LimitationsSkeleton';
import { getDirtyValues } from '@src/utils';
import { useNavigate } from 'react-router-dom';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

interface ITransactionModal {
  closeModal: () => void;
}

const TransactionModal: FC<ITransactionModal> = ({ closeModal }) => {
  const {
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting, defaultValues, isValid },

    control,
    watch,
    trigger,
  } = useForm<TransactionModalData>({
    defaultValues: TransactionModalDefaultValues,
    mode: 'all',
  });

  const [getBusinessTypes] = useLazyGetBusinessTypesQuery();
  const navigate = useNavigate();

  const [
    createUpdateTransactionsHistory,
    { isLoading: createUpdateTransactionLoading },
  ] = useCreateUpdateTransactionsHistoryMutation();

  const { businessTypes } = useAppSelector(selectSelects);

  const createUpdateTransactions = async (values: TransactionModalData) => {
    const changedValues = getDirtyValues({
      dirtyFields,
      values,
      maskedInputs,
      defaultValues,
      selectFields,
    });

    // TODO: think about this
    changedValues.methodVerified = 3;

    if (changedValues.length < 1) return;

    try {
      const res = await createUpdateTransactionsHistory({
        data: changedValues,
        method: 'POST',
        id: null,
      });

      if (res.error) {
        throw res.error;
      }

      window.open('/members/information', '_blank');
    } catch (err) {
      toastError(err, toastMessage.MEMBER.failedToCreateTransaction);
      logger.log(err, 'error');
    }
  };

  const isLoading = createUpdateTransactionLoading;

  return (
    <Modal
      dataTestId="hm-transaction-modal"
      closeClick={closeModal}
      title="Eligibility Transaction"
      maxWidth="420px"
      className="benefits_transaction_modal"
    >
      {isLoading ? (
        <LimitationsSkeleton />
      ) : (
        <div
          data-test-id="hm-transaction-modal-body"
          className="benefits-transaction-edit"
        >
          <div className="benefits-transaction-edit-body">
            <div className="benefits-transaction-edit-item">
              <ControlledDateSelect
                control={control}
                name="effectiveDate"
                trigger={trigger}
                rules={{ required: true }}
                title="Effective Date"
                dataTestId="member-create-effectiveDate"
                maxDate={new Date(watch('expirationDate'))}
              />
            </div>
            <div
              className="benefits-transaction-edit-item"
              data-test-id="modal-expiration-date"
            >
              <ControlledDateSelect
                dataTestId="modal-expiration-date-select"
                control={control}
                trigger={trigger}
                rules={{ required: true }}
                name="expirationDate"
                title="Expiration Date"
                minDate={new Date(watch('effectiveDate'))}
              />
            </div>
            <div className="benefits-transaction-edit-item" />
            <div
              className="benefits-transaction-edit-item"
              data-test-id="modal-plan-code"
            >
              <ControlledInput
                dataTestId="modal-plan-code-input"
                name="planCode"
                control={control}
                trigger={trigger}
                info={{ desc: '', toggle: true }}
                rules={{
                  required: validations.required('Plan Code'),
                }}
                title="Plan Code"
                type="alphanumeric"
              />
            </div>
            <div
              className="benefits-transaction-edit-item"
              data-test-id="modal-group-code"
            >
              <ControlledInput
                dataTestId="modal-group-code-input"
                name="groupCode"
                control={control}
                trigger={trigger}
                info={{ desc: '', toggle: true }}
                rules={{
                  required: validations.required('Group Code'),
                }}
                title="Group Code"
                type="alphanumeric"
              />
            </div>
            <div
              className="benefits-transaction-edit-item"
              data-test-id="modal-line-of-business"
            >
              <ControlledSelect
                dataTestId="modal-line-of-business-select"
                control={control}
                name="lineOfBusiness"
                title="LOB"
                placeholder="Select"
                trigger={trigger}
                selectOptions={businessTypes}
                rules={{ validate: validations.selectRequired('LOB') }}
                requestByAsync={() => getBusinessTypes({})}
              />
            </div>
          </div>

          <div
            className="transaction-modal-edit-footer"
            data-test-id="modal-submit-button"
          >
            <SubmitButton
              dataTestId="modal-submit-button-btn"
              dirtyFields={dirtyFields}
              title="Save"
              className="transaction-modal-btn"
              loading={isSubmitting}
              disabled={isSubmitting}
              disableWhenUnchangedFields
              onClick={handleSubmit(createUpdateTransactions)}
              forceDisable={!isValid}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TransactionModal;
